import React from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Componente de Paginação para Grids
 * Integra com o formato de resposta do backend paginado
 */
const GridPagination = ({ 
  pagination, 
  onPageChange, 
  loading = false,
  className = ""
}) => {
  if (!pagination || pagination.total === 0) return null;

  const { page, limit, total, total_pages, has_next, has_prev } = pagination;

  // Calcular páginas visíveis (máximo 5)
  const getVisiblePages = () => {
    const maxVisible = 5;
    if (total_pages <= maxVisible) {
      return Array.from({ length: total_pages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, page - half);
    let end = Math.min(total_pages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();
  const startRecord = ((page - 1) * limit) + 1;
  const endRecord = Math.min(page * limit, total);

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-2 py-3 border-t ${className}`}>
      {/* Info de registros */}
      <div className="text-sm text-gray-600">
        Mostrando <span className="font-semibold text-gray-900">{startRecord}</span> - {' '}
        <span className="font-semibold text-gray-900">{endRecord}</span> de {' '}
        <span className="font-semibold text-gray-900">{total}</span> registros
      </div>

      {/* Controles de paginação */}
      <div className="flex items-center gap-1">
        {/* Primeira página */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={!has_prev || loading}
          className="hidden sm:flex h-8 w-8 p-0"
          title="Primeira página"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>

        {/* Página anterior */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!has_prev || loading}
          className="h-8 w-8 p-0"
          title="Página anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Números de página - Desktop */}
        <div className="hidden sm:flex items-center gap-1">
          {visiblePages[0] > 1 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(1)}
                disabled={loading}
                className="h-8 w-8 p-0"
              >
                1
              </Button>
              {visiblePages[0] > 2 && (
                <span className="px-1 text-gray-400">...</span>
              )}
            </>
          )}

          {visiblePages.map((pageNum) => (
            <Button
              key={pageNum}
              variant={pageNum === page ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              className={`h-8 w-8 p-0 ${pageNum === page ? "bg-blue-600 text-white hover:bg-blue-700" : ""}`}
            >
              {pageNum}
            </Button>
          ))}

          {visiblePages[visiblePages.length - 1] < total_pages && (
            <>
              {visiblePages[visiblePages.length - 1] < total_pages - 1 && (
                <span className="px-1 text-gray-400">...</span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(total_pages)}
                disabled={loading}
                className="h-8 w-8 p-0"
              >
                {total_pages}
              </Button>
            </>
          )}
        </div>

        {/* Indicador de página - Mobile */}
        <span className="sm:hidden text-sm text-gray-600 px-3 font-medium">
          {page} / {total_pages}
        </span>

        {/* Próxima página */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!has_next || loading}
          className="h-8 w-8 p-0"
          title="Próxima página"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Última página */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(total_pages)}
          disabled={!has_next || loading}
          className="hidden sm:flex h-8 w-8 p-0"
          title="Última página"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default GridPagination;
